import { useCallback, useEffect, useState } from "react";

import { config } from "config";
import {
  useDeleteCloudinaryImageMutation,
  useGenerateCloudinarySignatureMutation,
} from "flows/Inbound/queries/photoUpload/photoUpload.generated";
import { type Photo } from "ui/PhotoUpload/PhotoUpload";

const { CLOUDINARY_UPLOAD_URL } = config.environment;

type PhotoMetadata = {
  secure_url: string;
  public_id: string;
  original_filename: string;
};

type SignatureData = {
  apiKey: string;
  signature: string;
  timestamp: number;
};

export function useCloudinaryPhotoUpload(folder: string, fileName?: string) {
  const [uploadedPhotos, setUploadedPhotos] = useState<Photo[]>([]);
  const [cloudinarySignatureData, setCloudinarySignatureData] = useState<SignatureData | null>(
    null,
  );

  const [generateSignature] = useGenerateCloudinarySignatureMutation({
    onCompleted: () => {},
    onError: () => {},
  });
  const [deleteImage] = useDeleteCloudinaryImageMutation({
    onCompleted: () => {},
    onError: () => {},
  });

  const uploadPhoto = async (file: File) => {
    const publicId = fileName;
    const signatureData = publicId
      ? await fetchCloudinarySignatureData(publicId)
      : (cloudinarySignatureData ??
        (() => {
          throw new Error("File upload configuration is missing");
        })());

    const { apiKey, signature, timestamp } = signatureData;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("api_key", apiKey);
    formData.append("timestamp", timestamp.toString());
    formData.append("signature", signature);
    formData.append("folder", folder);
    if (publicId) {
      formData.append("public_id", publicId);
    }

    const uploadFileResponse = await fetch(CLOUDINARY_UPLOAD_URL, {
      method: "POST",
      body: formData,
    });

    if (!uploadFileResponse.ok) {
      throw new Error("Error while uploading the file. Please try again.");
    }

    const photoMetadata: PhotoMetadata = await uploadFileResponse.json();

    setUploadedPhotos((prev) => [
      ...prev,
      {
        id: photoMetadata.public_id,
        src: photoMetadata.secure_url,
        title: photoMetadata.original_filename,
        status: "success",
      },
    ]);
  };

  const removePhoto = async (id: string) => {
    setUploadedPhotos((prev) =>
      prev.map((photo) => (photo.id === id ? { ...photo, status: "loading" } : photo)),
    );
    const responseDelete = await deleteImage({
      variables: { input: { publicId: id } },
    });

    if (!responseDelete.data?.deleteCloudinaryImage.success) {
      throw new Error("Error while deleting the photo. Please try again.");
    }

    setUploadedPhotos((prev) => prev.filter((photo) => photo.id !== id));
  };

  const fetchCloudinarySignatureData = useCallback(
    async (publicId?: string): Promise<SignatureData> => {
      const response = await generateSignature({
        variables: { input: { folder, ...(publicId ? { publicId } : {}) } },
      });
      if (!response.data?.generateCloudinarySignature) {
        throw new Error("File upload configuration is missing");
      }
      return response.data.generateCloudinarySignature;
    },
    [generateSignature, folder],
  );

  useEffect(() => {
    if (!fileName && cloudinarySignatureData === null) {
      fetchCloudinarySignatureData().then(setCloudinarySignatureData);
    }
  }, [fileName, cloudinarySignatureData, fetchCloudinarySignatureData]);

  return {
    uploadedPhotos,
    uploadPhoto,
    removePhoto,
  };
}
